import AddIcon from '@atlaskit/icon/core/add';
import type { FileIdentifier } from '@atlaskit/media-client';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, Flex, Pressable, Text, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { usePasteListener } from '@databases/copy-paste/usePasteListener';
import { messages } from '@databases/intl/messages';
import { useMediaClient } from '@databases/media-client';
import {
  isTemplateImageCover,
  getTemplateImageIdentifier,
  getTemplateImageCoverFileName
} from '@databases/templates/utils';
import {
  type DatabaseAttachment,
  DatabaseFieldType,
  type RawDatabaseValue
} from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { legacyColors as colors } from '@databases/utils/legacy-colors';
import { generateId } from '@databases/utils/utils';
import React, {
  forwardRef,
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Dropzone, { type DropzoneRef, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { isMigrationPlaceholderMediaId } from '@canvas/media-id';

import { useAttachmentPaste } from '../../hooks/use-attachment-paste.ts';
import {
  useAttachmentUpload,
  createNewAttachmentValue
} from '../../hooks/use-attachment-upload.ts';
import { useMediaCollectionId } from '../../hooks/use-media-collection-id.ts';
import GridItem from '../GridItem.tsx';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection.tsx';
import { PopoverEdit } from '../PopoverEdit.tsx';
import { type EditComponentProps } from '../types.ts';

const gridContainerStyles = xcss({
  display: 'grid',
  gap: 'space.200',
  padding: 'space.050',
  paddingTop: 'space.300',
  width: '100%',
  maxHeight: '400px',
  overflow: 'auto'
});

const twoColumnGridStyles = xcss({
  gridTemplateColumns: 'repeat(2, calc(50% - 8px))'
});

const threeColumnGridStyles = xcss({
  gridTemplateColumns: 'repeat(3, calc(33% - 9px))'
});

const gridWrapperStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  alignItems: 'center',
  width: `calc(100% - ${token('space.075', '0.375rem')})`,
  borderRadius: 'radius.small',
  height: `calc(100% - ${token('space.075', '0.375rem')})`,
  transition: `border 0.2s`
});

const getFileDropStyles = (isDragActive: boolean) => {
  return xcss({
    backgroundColor: isDragActive
      ? 'color.background.accent.blue.subtlest.hovered'
      : undefined
  });
};

const pressableStyles = xcss({
  width: '100%',
  padding: 'space.100',
  backgroundColor: 'color.background.neutral.subtle',

  ':hover': {
    background: token('color.background.neutral.subtle.hovered')
  }
});

const currentDataSectionStyles = xcss({
  padding: 'space.100'
});

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];

interface FileDropZoneType {
  children: React.ReactNode;
  id: string;
  index: number;
  handleChangeFile: (val: Value, i: number) => void;
  onDropNewFile: (file: File, i: number) => Promise<string | undefined>;
  noDrag: boolean;
  handleAddFile: (val: Value) => void;
  endIndex: number;
}

const FileDropzoneWrapper = forwardRef<DropzoneRef, FileDropZoneType>(
  function FileDropZone(
    {
      children,
      id,
      index,
      handleChangeFile,
      onDropNewFile,
      noDrag,
      handleAddFile,
      endIndex
    },
    ref
  ) {
    return (
      <Dropzone
        ref={ref}
        noClick
        noDrag={noDrag}
        onDropAccepted={async (acceptedFiles) => {
          const placeholderValue =
            acceptedFiles.length > 1 ? createNewAttachmentValue() : null;
          if (placeholderValue) {
            handleAddFile(placeholderValue);
          }
          const promises = acceptedFiles.map(async (file, i) => {
            const fileId = await onDropNewFile(file, i === 0 ? index : endIndex);
            if (!fileId) {
              return;
            }
            if (i === 0) {
              handleChangeFile({ _id: id, fileId }, index);
            } else if (i === 1 && placeholderValue) {
              handleChangeFile({ _id: placeholderValue._id, fileId }, endIndex);
            } else {
              handleAddFile({ _id: generateId(), fileId });
            }
          });
          await Promise.all(promises);
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div
              {...getRootProps({
                style: {
                  backgroundColor: isDragActive ? colors.B50 : 'transparent'
                }
              })}
              // FIXME: Accessibility issue - jsx-a11y/no-noninteractive-tabindex
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/tabindex-no-positive
              tabIndex={isDragActive ? 1 : -1}
            >
              <input {...getInputProps()} />
              {children}
            </div>
          );
        }}
      </Dropzone>
    );
  }
);

const gridItemKey = Symbol('grid-item');

type GridItemData = {
  item: Value;
  index: number;
  instanceId: symbol;
};

type GetGridItemDataResult = GridItemData & {
  [gridItemKey]: true;
};

function getGridItemData({
  item,
  index,
  instanceId
}: GridItemData): GetGridItemDataResult {
  return {
    [gridItemKey]: true,
    item,
    index,
    instanceId
  };
}

function isGridItemData(data: Record<string | symbol, unknown>): data is GridItemData {
  return data && typeof data === 'object' && gridItemKey in data;
}

const AttachmentPopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ATTACHMENT>
> = ({ contentId, onChange, onBlur, field, entryId, value, isBulkEdit }) => {
  const mediaClient = useMediaClient(contentId);
  const [uploadOrder, setUploadOrder] = useState<number[]>([]);
  const { formatMessage } = useIntl();
  const uiBrieImprovementsEnabled = isFeatureFlagEnabled('uiBrieImprovements');
  const arrayValue = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  usePopoverSoftSelection({ entryId, fieldId: field.id, isRenderedInSidePanel: false });
  const hasCurrentData = value.length > 0;

  const length = arrayValue.length;
  const mediaFormat = field.settings.mediaFormat;

  const [fileNames, setFileNames] = useState<Record<string, string>>({});

  const [instanceId] = useState(() => Symbol('grid-instance-id'));
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const { mediaCollectionId } = useMediaCollectionId(contentId);

  useEffect(() => {
    if (!mediaClient || !mediaCollectionId) {
      return;
    }

    arrayValue.forEach((item) => {
      const fileId = 'fileId' in item ? item.fileId : '';
      if (fileId && !fileNames[fileId]) {
        if (isTemplateImageCover(fileId)) {
          setFileNames((prev) => ({
            ...prev,
            [fileId]: getTemplateImageCoverFileName(fileId)
          }));
        } else if (!uiBrieImprovementsEnabled || !isMigrationPlaceholderMediaId(fileId)) {
          mediaClient.file
            .getFileState(fileId, {
              collectionName: mediaCollectionId
            })
            .subscribe({
              next: (fileState) => {
                if (
                  (fileState.status === 'processing' ||
                    fileState.status === 'processed') &&
                  fileState.name
                ) {
                  setFileNames((prev) => ({
                    ...prev,
                    [fileId]: fileState.name
                  }));
                }
              },
              error: (error) => {
                console.warn(`Error fetching filename for ${fileId}:`, error);
                setFileNames((prev) => ({
                  ...prev,
                  [fileId]: 'Attachment ' + fileId.substring(0, 5)
                }));
              }
            });
        }
      }
    });
  }, [mediaClient, mediaCollectionId, arrayValue, fileNames, uiBrieImprovementsEnabled]);

  const isUploading = uploadOrder.length > 0;

  const { handleDroppedFiles, onDropNewFile } = useAttachmentUpload({
    contentId,
    arrayValue,
    mediaCollectionId,
    setUploadOrder,
    handleAddFile: (file: Value) => onItemAdd(file, arrayValue),
    handleChangeFile: (file: Value, index: number) =>
      onItemChange(file, index, null, arrayValue)
  });

  type ValueElement =
    | (DatabaseAttachment & { _id: string })
    | (File & { _id: string })
    | { src: string; _id: string }
    | { fileId: string; _id: string };

  const getMediaFiles = useCallback(() => {
    return arrayValue.map((file: ValueElement) => {
      if ('fileId' in file && isTemplateImageCover(file.fileId)) {
        return getTemplateImageIdentifier(file.fileId);
      }

      return {
        mediaItemType: 'file',
        id: 'fileId' in file ? file.fileId : '',
        collectionName: mediaCollectionId
      } as FileIdentifier;
    });
  }, [mediaCollectionId, arrayValue]);

  const onItemChange = useCallback(
    (value: Value, index: number, _: null, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.EDIT,
          value,
          index
        }
      });
    },
    [onChange]
  );

  const onItemAdd = useCallback(
    (value: Value, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: value
        }
      });
    },
    [onChange]
  );

  const onItemDelete = useCallback(
    (_: Value, index: number, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index
        }
      });
    },
    [onChange]
  );

  const handleDelete = useCallback(
    (index: number) => {
      onItemDelete(arrayValue[index], index, arrayValue);
    },
    [onItemDelete, arrayValue]
  );
  const handleChangeFile = useCallback(
    (file: Value, index: number) => {
      onItemChange(file, index, null, arrayValue);
    },
    [onItemChange, arrayValue]
  );
  const handleAddFile = useCallback(
    (file: Value) => {
      onItemAdd(file, arrayValue);
    },
    [onItemAdd, arrayValue]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onFileDialogCancel: () => {
      setIsFileDialogOpen(false);
      onBlur?.();
    },
    noClick: true,
    disabled: uploadOrder.length > 0,
    noDrag: false,
    onDropAccepted: useCallback(
      (acceptedFiles: File[]) => {
        handleDroppedFiles(acceptedFiles);
        setIsFileDialogOpen(false);
        browseOrDropButtonRef.current?.focus();
      },
      [handleDroppedFiles]
    )
  });

  const { handlePaste: onPaste } = useAttachmentPaste({
    entryId,
    fieldId: field.id,
    handleDroppedFiles
  });

  usePasteListener({
    onPaste
  });

  const dropzoneRefsMap = useMemo(
    () => new Map(arrayValue.map((item) => [item._id, React.createRef<DropzoneRef>()])),
    [arrayValue]
  );
  const browseOrDropButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) {
        return;
      }

      const newValues = [...arrayValue];
      const [movedItem] = newValues.splice(fromIndex, 1);
      newValues.splice(toIndex, 0, movedItem);

      onChange(newValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex: fromIndex,
          newIndex: toIndex
        }
      });

      setTimeout(() => {
        const element = document.getElementById(`grid-item-${movedItem._id}`);
        if (element) {
          triggerPostMoveFlash(element);
        }
      }, 0);
    },
    [onChange, arrayValue]
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isGridItemData(source.data) && source.data.instanceId === instanceId;
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const targetData = target.data;
        if (!isGridItemData(source.data) || !isGridItemData(targetData)) {
          return;
        }

        const closestEdge = extractClosestEdge(targetData);
        const startIndex = source.data.index;
        const targetIndex = targetData.index;

        const GRID_COLS = 2;

        const targetRow = Math.floor(targetIndex / GRID_COLS);
        const targetCol = targetIndex % GRID_COLS;

        let destRow = targetRow;
        let destCol = targetCol;

        switch (closestEdge) {
          case 'left':
            destCol = targetCol;
            break;
          case 'right':
            destCol = targetCol + 1;
            if (destCol >= GRID_COLS) {
              destCol = 0;
              destRow = targetRow + 1;
            }
            break;
          case 'top':
            destRow = targetRow;
            break;
          case 'bottom':
            destRow = targetRow + 1;
            break;
        }

        let finishIndex = destRow * GRID_COLS + destCol;

        if (finishIndex >= length) {
          finishIndex = length - 1;
        }

        finishIndex = Math.max(0, Math.min(finishIndex, length - 1));

        if (finishIndex === startIndex) {
          return;
        }
        handleReorder(startIndex, finishIndex);
      }
    });
  }, [instanceId, handleReorder, length, arrayValue]);

  const handleButtonClick = useCallback(() => {
    setIsFileDialogOpen(true);
    open();
  }, [open]);

  const handleCardSelect = useCallback((cardId: string) => {
    setSelectedCardId((prevId) => (prevId === cardId ? null : cardId));
  }, []);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (isFileDialogOpen) {
        e.stopPropagation();
      }
    },
    [isFileDialogOpen]
  );

  const CurrentDataSection = useMemo(() => {
    return (
      <Flex
        testId="attachment-value-popover-edit"
        alignItems="center"
        xcss={[currentDataSectionStyles, getFileDropStyles(isDragActive)]}
      >
        <Box xcss={gridWrapperStyles}>
          <Box
            xcss={[
              gridContainerStyles,
              field.width && field.width > 528
                ? threeColumnGridStyles
                : twoColumnGridStyles
            ]}
          >
            {arrayValue.map((item, index) => {
              return (
                <FileDropzoneWrapper
                  key={item._id}
                  ref={dropzoneRefsMap.get(item._id) || React.createRef()}
                  id={item._id}
                  index={index}
                  handleChangeFile={handleChangeFile}
                  onDropNewFile={onDropNewFile}
                  noDrag
                  handleAddFile={handleAddFile}
                  endIndex={length}
                >
                  <GridItem
                    value={item}
                    index={index}
                    fileNames={fileNames}
                    handleChangeFile={handleChangeFile}
                    onDropNewFile={onDropNewFile}
                    handleDelete={handleDelete}
                    contentId={contentId}
                    uploadOrder={uploadOrder}
                    getMediaFiles={getMediaFiles}
                    mediaFormat={mediaFormat}
                    instanceId={instanceId}
                    draggingItemId={draggingItemId}
                    setDraggingItemId={setDraggingItemId}
                    dropzoneRef={dropzoneRefsMap.get(item._id) || React.createRef()}
                    gridItemKey={gridItemKey}
                    getGridItemData={getGridItemData}
                    isSelected={selectedCardId === item._id}
                    onSelect={handleCardSelect}
                    browseOrDropButtonRef={browseOrDropButtonRef}
                  />
                </FileDropzoneWrapper>
              );
            })}
          </Box>
        </Box>
      </Flex>
    );
  }, [
    arrayValue,
    contentId,
    draggingItemId,
    dropzoneRefsMap,
    field.width,
    fileNames,
    getMediaFiles,
    handleAddFile,
    handleCardSelect,
    handleChangeFile,
    handleDelete,
    instanceId,
    isDragActive,
    length,
    mediaFormat,
    onDropNewFile,
    selectedCardId,
    uploadOrder
  ]);

  const AddDataSection = useMemo(() => {
    return (
      <Box xcss={getFileDropStyles(!hasCurrentData && isDragActive)}>
        <Pressable
          onClick={handleButtonClick}
          onBlur={handleBlur}
          ref={browseOrDropButtonRef}
          autoFocus
          xcss={pressableStyles}
        >
          <Inline alignBlock="center" space="space.050">
            <AddIcon
              label=""
              size="medium"
              spacing="spacious"
              color={token('color.icon')}
            />
            <Text color="color.text.subtle">
              {formatMessage(messages['media.browse-or-drop-file'])}
            </Text>
          </Inline>
        </Pressable>
      </Box>
    );
  }, [formatMessage, handleBlur, handleButtonClick, hasCurrentData, isDragActive]);

  const popoverEdit = useMemo(() => {
    return (
      <PopoverEdit
        isMultiSelect
        hasCurrentData={hasCurrentData}
        currentDataSection={CurrentDataSection}
        addDataSection={AddDataSection}
        isRenderedInSidePanel={false}
        fieldWidth={field.width}
        additionalXCSS={getFileDropStyles(isDragActive)}
        allowCurrentDataSectionClick
        isBulkEdit={isBulkEdit}
      />
    );
  }, [
    AddDataSection,
    CurrentDataSection,
    field.width,
    hasCurrentData,
    isBulkEdit,
    isDragActive
  ]);

  if (isUploading) {
    return popoverEdit;
  }

  return (
    // Wrap Popover edit with Dropzone props to allow for
    // dragging and dropping a file from some file directory
    // over all contents of the popover. The input tag enables
    // choosing a file on button click.
    <Box {...getRootProps()}>
      <input {...getInputProps()} />
      {popoverEdit}
    </Box>
  );
};

export default AttachmentPopoverEdit;
